import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { Guid } from "guid-string";
import { AsyncLoadResult, AsyncLoadOptions } from '../../../../shared/abstractStore';
import { launchAssessmentSessionViewModelQuery, launchAssessmentSessionViewModelQueryVariables } from '../../generated/launchAssessmentSessionViewModelQuery';
import { assessmentSessionFieldsFragment } from '../../models/AssessmentSession';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { questionFieldsFragment } from '../../models/Question';
import { assessmentFieldsFragment } from '../../models/Assessment';
import { assessmentFeedbackFieldsFragment } from '../../models/AssessmentFeedback';
import { questionAnswerFieldsFragment } from '../../models/QuestionAnswer';
import { questionFeedbackFieldsFragment } from '../../models/QuestionFeedback';
import { blobReferenceFieldsFragment } from '../../models/BlobReference';
import { questionResponseEventFieldsFragment } from '../../models/QuestionResponseEvent';
import { assessmentItemFieldsFragment } from '../../models/AssessmentItem';
import { assessmentItemQuestionFieldsFragment } from '../../models/AssessmentItemQuestion';
import { questionSequenceQuestionFieldsFragment } from '../../models/QuestionSequenceQuestion';
import { subscriptionFieldsFragment } from '../../models/Subscription';

/**
 * Get everything needed to lauch an AssessmentSession.
 * @param id
 * @param options
 */
export function useLaunchAssessmentSessionViewModel(id: string | undefined | null, options: AsyncLoadOptions = {}): AsyncLoadResult<launchAssessmentSessionViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch }] = useLazyQuery<launchAssessmentSessionViewModelQuery, launchAssessmentSessionViewModelQueryVariables>(
        gql`
        query launchAssessmentSessionViewModelQuery ($id: ID!) {
            viewModel: launchAssessmentSessionViewModel(id: $id) {
                id
                assessmentSession {
                    ...assessmentSessionFields
                }
                assessment {
                    ...assessmentFields
                }
                assessmentFeedbacks {
                    ...assessmentFeedbackFields
                }
                questions {
                    ...questionFields
                }
                questionSequenceQuestions {
                    ...questionSequenceQuestionFields

                    childQuestion {
                        ...questionFields
                    }
                }
                questionnaireQuestionAnswers {
                    ...questionAnswerFields
                }
                questionAnswers {
                    ...questionAnswerFields
                }
                questionFeedbacks {
                    ...questionFeedbackFields
                }
                blobReferences {
                    ...blobReferenceFields
                }
                questionResponseEvents {
                    ...questionResponseEventFields
                }
                assessmentItems {
                    ...assessmentItemFields
                }
                assessmentItemQuestions {
                    ...assessmentItemQuestionFields
                }
                assessmentItemSessionOrders {
                    id
                    questionIds {
                        id
                    }
                }
                subscription {
                    ...subscriptionFields
                }
            }
        }

        ${assessmentSessionFieldsFragment}
        ${assessmentFieldsFragment}
        ${assessmentFeedbackFieldsFragment}
        ${questionFieldsFragment}
        ${questionAnswerFieldsFragment}
        ${questionFeedbackFieldsFragment}
        ${blobReferenceFieldsFragment}
        ${questionResponseEventFieldsFragment}
        ${assessmentItemFieldsFragment}
        ${assessmentItemQuestionFieldsFragment}
        ${questionSequenceQuestionFieldsFragment}
        ${subscriptionFieldsFragment}
        `,
        {
            variables: {
                id: id ?? Guid.empty
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error
    }), [data, refresh, loading, error]);
}
